"use client";

import { IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";

interface ArrayTagInputProps {
  value: unknown;
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  embedTagsInInput?: boolean;
  caseInsensitiveDedup?: boolean; // Twitter handles, hashtags, emails — match semantics are case-insensitive.
}

export interface ArrayTagInputHandle {
  flushPendingValue: () => string[];
}

const ArrayTagInput = forwardRef<ArrayTagInputHandle, ArrayTagInputProps>(function ArrayTagInput(
  { value, onChange, placeholder, className, embedTagsInInput = false, caseInsensitiveDedup = false },
  ref,
) {
  const [inputValue, setInputValue] = useState("");
  const items = useMemo<string[]>(() => (Array.isArray(value) ? value : []), [value]);
  const inputRef = useRef<HTMLInputElement>(null);

  const flushPendingValue = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return items;
    }

    const duplicate = caseInsensitiveDedup
      ? items.some(item => item.toLowerCase() === trimmed.toLowerCase())
      : items.includes(trimmed);
    if (duplicate) {
      setInputValue("");
      return items;
    }

    const nextItems = [...items, trimmed];
    onChange(nextItems);
    setInputValue("");
    return nextItems;
  }, [inputValue, items, onChange, caseInsensitiveDedup]);

  useImperativeHandle(
    ref,
    () => ({
      flushPendingValue,
    }),
    [flushPendingValue],
  );

  const addItem = () => {
    flushPendingValue();
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  if (embedTagsInInput) {
    return (
      <div
        className={cn(
          "border-ds-border-default bg-ds-surface-card flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm transition-colors focus-within:border-zinc-400 dark:focus-within:border-zinc-500",
          className,
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {items.map((item, i) => (
          <Badge
            key={`${item}-${i}`}
            variant="default"
            className="max-w-full gap-1 rounded-full px-2.5 py-1 text-xs font-medium normal-case tracking-normal"
          >
            <span className="truncate">{item}</span>
            <button
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={e => {
                e.stopPropagation();
                removeItem(i);
              }}
              className="hover:bg-ds-neutral-300 rounded-full p-0.5 dark:hover:bg-zinc-600"
            >
              <IconXmarkOutline18 className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addItem();
            } else if (e.key === "Backspace" && !inputValue && items.length > 0) {
              e.preventDefault();
              removeItem(items.length - 1);
            }
          }}
          onBlur={addItem}
          placeholder={items.length === 0 ? placeholder || "Type and press Enter to add" : ""}
          className="text-ds-text-primary placeholder:text-ds-text-muted dark:placeholder:text-ds-text-disabled min-w-[120px] flex-1 border-0 bg-transparent p-0 text-sm outline-none"
        />
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {items.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {items.map((item, i) => (
            <Badge key={`${item}-${i}`} variant="default" className="gap-1 pr-1 normal-case">
              {item}
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => removeItem(i)}
                className="hover:bg-ds-neutral-300 rounded-full p-0.5 dark:hover:bg-zinc-600"
              >
                <IconXmarkOutline18 className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addItem();
          }
        }}
        onBlur={addItem}
        placeholder={placeholder || "Type and press Enter to add"}
        className="text-ds-text-primary"
      />
    </div>
  );
});

export default ArrayTagInput;
